/**
 * Durable form submit + global feedback bus.
 * Uses a dedicated Worker so requests keep going if the user clicks Back / leaves the page.
 */
import type { SilentSubmitRequest, SilentSubmitResponse } from './silentSubmit.worker';

export type SubmitFeedback = {
  phase: 'processing' | 'success' | 'error';
  title: string;
  message: string;
} | null;

type JobMeta = {
  successTitle: string;
  errorTitle: string;
};

const listeners = new Set<(feedback: SubmitFeedback) => void>();
let feedback: SubmitFeedback = null;
const jobs = new Map<string, JobMeta>();

let worker: Worker | null = null;
let workerFailed = false;

function emit(next: SubmitFeedback) {
  feedback = next;
  listeners.forEach((cb) => cb(feedback));
}

export function getSubmitFeedback() {
  return feedback;
}

export function subscribeSubmitFeedback(cb: (feedback: SubmitFeedback) => void) {
  listeners.add(cb);
  cb(feedback);
  return () => {
    listeners.delete(cb);
  };
}

export function clearSubmitFeedback() {
  emit(null);
}

export function showSubmitFeedback(next: NonNullable<SubmitFeedback>) {
  emit(next);
}

function ensureWorker() {
  if (worker || workerFailed || typeof window === 'undefined') return worker;
  try {
    worker = new Worker(new URL('./silentSubmit.worker.ts', import.meta.url), { type: 'module' });
    worker.onmessage = (event: MessageEvent<SilentSubmitResponse>) => {
      const { id, ok, message } = event.data;
      const meta = jobs.get(id);
      jobs.delete(id);
      if (!meta) return;
      emit({
        phase: ok ? 'success' : 'error',
        title: ok ? meta.successTitle : meta.errorTitle,
        message,
      });
    };
    worker.onerror = () => {
      workerFailed = true;
      worker = null;
    };
  } catch {
    workerFailed = true;
    worker = null;
  }
  return worker;
}

async function fallbackFetch(url: string, body: string, meta: JobMeta, id: string) {
  try {
    const res = await fetch(url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body,
      keepalive: true,
      credentials: 'same-origin',
    });
    let message = res.ok ? 'Submitted successfully.' : 'Submission failed.';
    let ok = res.ok;
    try {
      const data = (await res.json()) as { ok?: boolean; message?: string };
      if (typeof data.message === 'string' && data.message) message = data.message;
      ok = Boolean(data.ok ?? res.ok);
    } catch {
      /* use defaults */
    }
    jobs.delete(id);
    emit({
      phase: ok ? 'success' : 'error',
      title: ok ? meta.successTitle : meta.errorTitle,
      message,
    });
  } catch {
    jobs.delete(id);
    emit({
      phase: 'error',
      title: meta.errorTitle,
      message: 'Unable to reach the server. Please try again.',
    });
  }
}

export type SilentSubmitOptions = {
  url: string;
  payload: unknown;
  processingTitle?: string;
  processingMessage?: string;
  successTitle?: string;
  errorTitle?: string;
};

/**
 * Shows the global processing popup and submits via Worker (or keepalive fetch).
 * Safe to navigate away after calling — the job continues in the background.
 */
export function silentJsonSubmit(options: SilentSubmitOptions) {
  const id =
    typeof crypto !== 'undefined' && 'randomUUID' in crypto
      ? crypto.randomUUID()
      : `job-${Date.now()}-${Math.random().toString(16).slice(2)}`;

  const meta: JobMeta = {
    successTitle: options.successTitle || 'Submission successful',
    errorTitle: options.errorTitle || 'Submission failed',
  };
  jobs.set(id, meta);

  emit({
    phase: 'processing',
    title: options.processingTitle || 'Sending…',
    message: options.processingMessage || 'Please wait while we submit your details. You can leave this page — we will keep processing.',
  });

  const body = JSON.stringify(options.payload);
  const w = ensureWorker();
  if (w) {
    const msg: SilentSubmitRequest = { id, url: options.url, body };
    w.postMessage(msg);
    return id;
  }

  void fallbackFetch(options.url, body, meta, id);
  return id;
}
